import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  FieldValue,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { db, retryOperation } from '../config';
import { v4 as uuidv4 } from 'uuid';

const COLLECTION_NAME = 'customers';

/**
 * Interface for a customer vessel
 */
export interface CustomerVessel {
  vesselId: string;
  registrationNumber: string;
  type: string;
  name?: string;
  make: string;
  model: string;
  year: number;
  length: number;
  color: string;
  mooringSlip?: string;
  status: 'active' | 'inactive' | 'deleted';
  addedAt: string | Timestamp | FieldValue;
  updatedAt?: string | Timestamp | FieldValue;
}

/**
 * Interface for customer data
 */
export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  email: string;
  phone: string;
  vessels: CustomerVessel[];
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
}

export interface CustomerInput {
  firstName: string;
  lastName: string;
  companyName?: string;
  email: string;
  phone: string;
  vessels?: CustomerVessel[];
}

/**
 * Get all customers with their vessels
 */
export const getAllCustomers = async (): Promise<Customer[]> => {
  return retryOperation(async () => {
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    return customersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      vessels: doc.data().vessels || []
    } as Customer));
  });
};

/**
 * Get a customer by ID
 */
export const getCustomerById = async (customerId: string): Promise<Customer | null> => {
  return retryOperation(async () => {
    const customerDoc = await getDoc(doc(db, COLLECTION_NAME, customerId));
    
    if (!customerDoc.exists()) {
      return null;
    }
    
    return {
      id: customerDoc.id,
      ...customerDoc.data(),
      vessels: customerDoc.data().vessels || []
    } as Customer;
  });
};

/**
 * Search customers by name, email, or phone
 */
export const searchCustomers = async (searchTerm: string): Promise<Customer[]> => {
  return retryOperation(async () => {
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    const customers = customersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Customer));
    
    if (!searchTerm) return customers;
    
    const searchTermLower = searchTerm.toLowerCase().trim();
    
    return customers.filter(customer => {
      const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
      const email = customer.email?.toLowerCase() || '';
      const phone = customer.phone || '';
      const companyName = customer.companyName?.toLowerCase() || '';
      
      // Check if the search term matches the full name exactly
      if (fullName === searchTermLower) {
        return true;
      }
      
      // Check if the search term is a partial match of the full name
      if (fullName.includes(searchTermLower)) {
        return true;
      }
      
      // Check if the search term matches individual fields
      return email.includes(searchTermLower) || 
             phone.includes(searchTermLower) ||
             companyName.includes(searchTermLower);
    });
  });
};

/**
 * Create a vessel for a customer
 */
export const createCustomerVessel = async (
  customerId: string, 
  vesselData: Omit<CustomerVessel, 'vesselId' | 'addedAt' | 'status'>
): Promise<string> => {
  return retryOperation(async () => {
    const customerRef = doc(db, COLLECTION_NAME, customerId);
    const customerDoc = await getDoc(customerRef);
    
    if (!customerDoc.exists()) {
      throw new Error(`Customer with ID ${customerId} not found`);
    }
    
    // Check for duplicate registration number
    const customerData = customerDoc.data();
    const customerVessels = customerData.vessels || [];
    const isDuplicate = customerVessels.some(
      (vessel: CustomerVessel) => vessel.registrationNumber === vesselData.registrationNumber
    );
    
    if (isDuplicate) {
      throw new Error(`A vessel with registration number ${vesselData.registrationNumber} already exists for this customer`);
    }
    
    // Generate a unique vessel ID
    const vesselId = uuidv4();
    
    // Create the vessel object with a standard timestamp instead of serverTimestamp
    const newVessel: CustomerVessel = {
      ...vesselData,
      vesselId,
      status: 'active',
      addedAt: new Date().toISOString() // Use ISO string instead of serverTimestamp
    };
    
    // Get existing vessels and add the new one
    const updatedVessels = [...customerVessels, newVessel];
    
    // Update the customer document with the new vessels array
    await updateDoc(customerRef, {
      vessels: updatedVessels,
      updatedAt: serverTimestamp()
    });
    
    return vesselId;
  });
};

/**
 * Update a vessel for a customer
 */
export const updateCustomerVessel = async (
  customerId: string,
  vesselId: string,
  vesselData: Partial<CustomerVessel>
): Promise<void> => {
  return retryOperation(async () => {
    const customerRef = doc(db, COLLECTION_NAME, customerId);
    const customerDoc = await getDoc(customerRef);
    
    if (!customerDoc.exists()) {
      throw new Error(`Customer with ID ${customerId} not found`);
    }
    
    const vessels = customerDoc.data().vessels || [];
    const vesselIndex = vessels.findIndex((v: CustomerVessel) => v.vesselId === vesselId);
    
    if (vesselIndex === -1) {
      throw new Error(`Vessel with ID ${vesselId} not found for customer ${customerId}`);
    }
    
    // Remove undefined fields from vesselData
    const cleanVesselData = Object.fromEntries(Object.entries(vesselData).filter(([_, v]) => v !== undefined));
    vessels[vesselIndex] = {
      ...vessels[vesselIndex],
      ...cleanVesselData,
      updatedAt: new Date().toISOString()
    };
    
    // Update the customer document
    await updateDoc(customerRef, {
      vessels,
      updatedAt: serverTimestamp()
    });
  });
};

/**
 * Delete a vessel for a customer (mark as deleted)
 */
export const deleteCustomerVessel = async (
  customerId: string,
  vesselId: string
): Promise<void> => {
  return retryOperation(async () => {
    const customerRef = doc(db, COLLECTION_NAME, customerId);
    const customerDoc = await getDoc(customerRef);
    
    if (!customerDoc.exists()) {
      throw new Error(`Customer with ID ${customerId} not found`);
    }
    
    const vessels = customerDoc.data().vessels || [];
    const vesselIndex = vessels.findIndex((v: CustomerVessel) => v.vesselId === vesselId);
    
    if (vesselIndex === -1) {
      throw new Error(`Vessel with ID ${vesselId} not found for customer ${customerId}`);
    }
    
    // Mark the vessel as deleted with a standard timestamp
    vessels[vesselIndex] = {
      ...vessels[vesselIndex],
      status: 'deleted',
      updatedAt: new Date().toISOString() // Use ISO string instead of serverTimestamp
    };
    
    // Update the customer document
    await updateDoc(customerRef, {
      vessels,
      updatedAt: serverTimestamp()
    });
  });
};

/**
 * Search for vessels by registration number
 */
export const searchVesselsByRegistration = async (
  registrationNumber: string
): Promise<{vessel: CustomerVessel, customerId: string, customer: Customer}[]> => {
  return retryOperation(async () => {
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    const results: {vessel: CustomerVessel, customerId: string, customer: Customer}[] = [];
    
    customersSnapshot.forEach(customerDoc => {
      const customer = {
        id: customerDoc.id,
        ...customerDoc.data()
      } as Customer;
      
      const vessels = customer.vessels || [];
      
      vessels.forEach(vessel => {
        if (vessel.registrationNumber?.toLowerCase() === registrationNumber.toLowerCase()) {
          results.push({
            vessel,
            customerId: customer.id,
            customer
          });
        }
      });
    });
    
    return results;
  });
};

/**
 * Get all vessels from all customers
 */
export const getAllCustomerVessels = async (): Promise<{vessel: CustomerVessel, customerId: string, customer: Customer}[]> => {
  return retryOperation(async () => {
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    const results: {vessel: CustomerVessel, customerId: string, customer: Customer}[] = [];
    
    customersSnapshot.forEach(customerDoc => {
      const customer = {
        id: customerDoc.id,
        ...customerDoc.data()
      } as Customer;
      
      const vessels = customer.vessels || [];
      
      vessels.forEach(vessel => {
        if (vessel.status !== 'deleted') {
          results.push({
            vessel,
            customerId: customer.id,
            customer
          });
        }
      });
    });
    
    return results;
  });
};

/**
 * Search for vessels by name
 */
export const searchVesselsByName = async (
  name: string
): Promise<{vessel: CustomerVessel, customerId: string, customer: Customer}[]> => {
  return retryOperation(async () => {
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    const results: {vessel: CustomerVessel, customerId: string, customer: Customer}[] = [];
    
    customersSnapshot.forEach(customerDoc => {
      const customer = {
        id: customerDoc.id,
        ...customerDoc.data()
      } as Customer;
      
      const vessels = customer.vessels || [];
      
      vessels.forEach(vessel => {
        if (vessel.name?.toLowerCase().includes(name.toLowerCase()) && vessel.status !== 'deleted') {
          results.push({
            vessel,
            customerId: customer.id,
            customer
          });
        }
      });
    });
    
    return results;
  });
};

/**
 * Add launch activity
 */
export const addLaunchActivity = async (
  vesselId: string, 
  activityData: {
    type: 'launch' | 'haul';
    trailerLicense: string;
    sealNumber: string;
    sealColor: string;
    aisSticker?: string;
    notes?: string;
    staffName: string;
  }
): Promise<string> => {
  return retryOperation(async () => {
    const activityRef = collection(db, 'launch_activity');
    const newActivityRef = doc(activityRef);
    
    await setDoc(newActivityRef, {
      vesselId,
      ...activityData,
      timestamp: serverTimestamp()
    });
    
    return newActivityRef.id;
  });
};

/**
 * Get launch activity for a vessel
 */
export const getVesselLaunchActivity = async (
  vesselId: string
): Promise<any[]> => {
  return retryOperation(async () => {
    const activityQuery = query(
      collection(db, 'launch_activity'),
      where('vesselId', '==', vesselId),
      orderBy('timestamp', 'desc')
    );
    
    const activitySnapshot = await getDocs(activityQuery);
    
    return activitySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : new Date(doc.data().timestamp)
    }));
  });
};

/**
 * Get today's launch activity
 */
export const getTodayLaunchActivity = async (): Promise<any[]> => {
  return retryOperation(async () => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const activityQuery = query(
      collection(db, 'launch_activity'),
      orderBy('timestamp', 'desc')
    );
    
    const activitySnapshot = await getDocs(activityQuery);
    
    return activitySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : new Date(doc.data().timestamp)
      }))
      .filter(activity => activity.timestamp >= startOfDay && activity.timestamp <= now);
  });
};

/**
 * Create a new customer
 */
export const createCustomer = async (customerData: CustomerInput): Promise<Customer> => {
  return retryOperation(async () => {
    const customerRef = doc(collection(db, COLLECTION_NAME));
    let newCustomer: Omit<Customer, 'id'> = {
      ...customerData,
      vessels: customerData.vessels || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    // Remove undefined fields (Firestore does not allow undefined values)
    newCustomer = Object.fromEntries(
      Object.entries(newCustomer).filter(([_, v]) => v !== undefined)
    ) as Omit<Customer, 'id'>;
    await setDoc(customerRef, newCustomer);
    return {
      id: customerRef.id,
      ...newCustomer
    };
  });
};

/**
 * Update an existing customer
 */
export const updateCustomer = async (
  customerId: string,
  customerData: Partial<Omit<Customer, 'id' | 'createdAt'>>
): Promise<void> => {
  return retryOperation(async () => {
    const customerRef = doc(db, COLLECTION_NAME, customerId);
    const customerDoc = await getDoc(customerRef);
    
    if (!customerDoc.exists()) {
      throw new Error(`Customer with ID ${customerId} not found`);
    }
    
    // Remove undefined fields
    const cleanData = Object.fromEntries(Object.entries(customerData).filter(([_, v]) => v !== undefined));
    await updateDoc(customerRef, {
      ...cleanData,
      updatedAt: serverTimestamp()
    });
  });
};

export const getAllVessels = async () => {
  try {
    const vesselsRef = collection(db, 'vessels');
    const querySnapshot = await getDocs(vesselsRef);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting vessels:', error);
    throw error;
  }
};

export const deleteCustomer = async (customerId: string): Promise<void> => {
  const customerRef = doc(db, COLLECTION_NAME, customerId);
  await deleteDoc(customerRef);
};

/**
 * Update launch activity
 */
export const updateLaunchActivity = async (
  id: string,
  activityData: Partial<{
    type: 'launch' | 'haul';
    trailerLicense: string;
    sealNumber: string;
    sealColor: string;
    aisSticker?: string;
    notes?: string;
    staffName: string;
    registrationNumber?: string;
    timestamp?: Date;
  }>
): Promise<void> => {
  return retryOperation(async () => {
    const activityRef = doc(db, 'launch_activity', id);
    // Remove undefined fields
    const cleanData = Object.fromEntries(Object.entries(activityData).filter(([_, v]) => v !== undefined));
    await updateDoc(activityRef, cleanData);
  });
};

/**
 * Delete launch activity
 */
export const deleteLaunchActivity = async (id: string): Promise<void> => {
  return retryOperation(async () => {
    const activityRef = doc(db, 'launch_activity', id);
    await deleteDoc(activityRef);
  });
};

/**
 * Check if a customer with the given first and last name exists
 */
export const findCustomerByName = async (firstName: string, lastName: string): Promise<Customer | null> => {
  return retryOperation(async () => {
    const customersRef = collection(db, COLLECTION_NAME);
    const q = query(
      customersRef,
      where('firstName', '==', firstName),
      where('lastName', '==', lastName)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const customerDoc = querySnapshot.docs[0];
    return {
      id: customerDoc.id,
      ...customerDoc.data(),
      vessels: customerDoc.data().vessels || []
    } as Customer;
  });
};

/**
 * Interface for duplicate customer groups
 */
export interface DuplicateCustomerGroup {
  customers: Customer[];
  matchType: 'name' | 'email' | 'phone';
  matchValue: string;
}

/**
 * Find potential duplicate customers in the collection
 */
export const findDuplicateCustomers = async (): Promise<DuplicateCustomerGroup[]> => {
  return retryOperation(async () => {
    const customersSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    const customers = customersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      vessels: doc.data().vessels || []
    } as Customer));

    const duplicateGroups: DuplicateCustomerGroup[] = [];
    const processedIds = new Set<string>();

    // Helper function to normalize strings for comparison
    const normalizeString = (str: string) => str.toLowerCase().trim().replace(/\s+/g, ' ');

    // Check for duplicates by name
    customers.forEach(customer => {
      if (processedIds.has(customer.id)) return;

      const normalizedName = normalizeString(`${customer.firstName} ${customer.lastName}`);
      const nameMatches = customers.filter(c => 
        c.id !== customer.id && 
        !processedIds.has(c.id) &&
        normalizeString(`${c.firstName} ${c.lastName}`) === normalizedName
      );

      if (nameMatches.length > 0) {
        duplicateGroups.push({
          customers: [customer, ...nameMatches],
          matchType: 'name',
          matchValue: normalizedName
        });
        processedIds.add(customer.id);
        nameMatches.forEach(m => processedIds.add(m.id));
      }
    });

    // Check for duplicates by email
    customers.forEach(customer => {
      if (!customer.email || processedIds.has(customer.id)) return;

      const normalizedEmail = normalizeString(customer.email);
      const emailMatches = customers.filter(c => 
        c.id !== customer.id && 
        !processedIds.has(c.id) &&
        c.email &&
        normalizeString(c.email) === normalizedEmail
      );

      if (emailMatches.length > 0) {
        duplicateGroups.push({
          customers: [customer, ...emailMatches],
          matchType: 'email',
          matchValue: normalizedEmail
        });
        processedIds.add(customer.id);
        emailMatches.forEach(m => processedIds.add(m.id));
      }
    });

    // Check for duplicates by phone
    customers.forEach(customer => {
      if (!customer.phone || processedIds.has(customer.id)) return;

      const normalizedPhone = customer.phone.replace(/\D/g, '');
      const phoneMatches = customers.filter(c => 
        c.id !== customer.id && 
        !processedIds.has(c.id) &&
        c.phone &&
        c.phone.replace(/\D/g, '') === normalizedPhone
      );

      if (phoneMatches.length > 0) {
        duplicateGroups.push({
          customers: [customer, ...phoneMatches],
          matchType: 'phone',
          matchValue: normalizedPhone
        });
        processedIds.add(customer.id);
        phoneMatches.forEach(m => processedIds.add(m.id));
      }
    });

    return duplicateGroups;
  });
};

/**
 * Merge multiple customer records into a primary customer record
 * @param primaryCustomerId The ID of the customer record to keep
 * @param customerIdsToMerge Array of customer IDs to merge into the primary record
 */
export const mergeCustomers = async (primaryCustomerId: string, customerIdsToMerge: string[]): Promise<void> => {
  return retryOperation(async () => {
    // Get all customers involved in the merge
    const [primaryCustomer, ...customersToMerge] = await Promise.all([
      getCustomerById(primaryCustomerId),
      ...customerIdsToMerge.map(id => getCustomerById(id))
    ]);

    if (!primaryCustomer) {
      throw new Error('Primary customer not found');
    }

    // Combine all vessels from merged customers
    const allVessels = [
      ...primaryCustomer.vessels,
      ...customersToMerge.flatMap(c => c?.vessels || [])
    ];

    // Remove duplicate vessels based on registration number
    const uniqueVessels = allVessels.reduce((acc, vessel) => {
      const existingIndex = acc.findIndex(v => 
        v.registrationNumber === vessel.registrationNumber
      );
      if (existingIndex === -1) {
        acc.push(vessel);
      }
      return acc;
    }, [] as CustomerVessel[]);

    // Update the primary customer with combined data
    await updateCustomer(primaryCustomerId, {
      ...primaryCustomer,
      vessels: uniqueVessels,
      // Keep the most recent data for other fields
      email: primaryCustomer.email || customersToMerge.find(c => c?.email)?.email,
      phone: primaryCustomer.phone || customersToMerge.find(c => c?.phone)?.phone,
      companyName: primaryCustomer.companyName || customersToMerge.find(c => c?.companyName)?.companyName
    });

    // Delete the merged customer records
    await Promise.all(
      customerIdsToMerge.map(id => deleteCustomer(id))
    );
  });
}; 